import React, { useState } from 'react';
import ProgramColorPickModal from '../ProgramColorPickModal';
import {
  PROGRAM_CELL_BG_ANIM_PRESETS,
  normalizeProgramCellBgValue,
  parseProgramCellBgAnimId,
  programCellBgAnimLabel,
  programCellBgAnimToken,
  programCellBgSwatchProps,
} from '../../utils/programCellBgStyle';

/** 글자가 잘 보이는 옅은 배경색 */
const PROGRAM_CELL_BG_PASTEL_PRESETS = [
  '#f6e8e8',
  '#f7ede1',
  '#f5f0dc',
  '#e9f0e1',
  '#e2f0eb',
  '#e3edf4',
  '#e7e9f5',
  '#eee7f2',
  '#f4e7ed',
  '#f0ece6',
  '#eaecef',
  '#f3f2e4',
];

/** 강조용 형광색 */
const PROGRAM_CELL_BG_NEON_PRESETS = [
  { hex: '#ffff00', label: '형광 노랑' },
  { hex: '#66ff66', label: '형광 초록' },
  { hex: '#ff9f1a', label: '형광 주황' },
  { hex: '#d580ff', label: '형광 보라' },
];

export interface ProgramCellBgColorModalProps {
  /** 적용 대상 행 수 */
  targetRowCount: number;
  registeredColors: string[];
  paletteBusy?: boolean;
  /** hex 또는 애니메이션 토큰(`anim:<id>`), null이면 지우기 */
  onApply: (value: string | null) => void;
  onRegisterColor: (value: string) => void;
  onReplaceColor: (fromHex: string, toHex: string) => void;
  onRemoveColor: (value: string) => void;
  onClose: () => void;
}

function swatchTitle(value: string): string {
  return programCellBgAnimLabel(value) ?? PROGRAM_CELL_BG_NEON_PRESETS.find((p) => p.hex === value)?.label ?? value;
}

const ProgramCellBgColorModal: React.FC<ProgramCellBgColorModalProps> = ({
  targetRowCount,
  registeredColors,
  paletteBusy = false,
  onApply,
  onRegisterColor,
  onReplaceColor,
  onRemoveColor,
  onClose,
}) => {
  const [selected, setSelected] = useState<string>(registeredColors[0] ?? PROGRAM_CELL_BG_PASTEL_PRESETS[0]);
  const [editingHex, setEditingHex] = useState<string | null>(null);
  const selectedIsAnim = parseProgramCellBgAnimId(selected) !== null;
  const selectedPreview = programCellBgSwatchProps(selected);

  const applySelected = () => {
    const v = normalizeProgramCellBgValue(selected);
    if (!v) return;
    if (!registeredColors.includes(v)) onRegisterColor(v);
    onApply(v);
  };

  const renderPresetSwatch = (value: string, title: string) => {
    const inner = programCellBgSwatchProps(value);
    return (
      <button
        key={value}
        type="button"
        role="option"
        aria-selected={selected === value}
        aria-label={title}
        className={'program-color-swatch program-color-swatch-btn' + (selected === value ? ' is-selected' : '')}
        onClick={() => setSelected(value)}
        title={title}
      >
        <span className={['program-color-swatch-inner', inner.className].filter(Boolean).join(' ')} style={inner.style} aria-hidden />
      </button>
    );
  };

  return (
    <>
      <div className="cp-modal-backdrop" onClick={onClose}>
        <div
          className="cp-modal program-color-modal cp-program-cell-bg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-program-cell-bg-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="cp-program-cell-bg-modal-title">셀 배경색</h3>
          <p className="page-desc">
            선택한 {targetRowCount}개 행의 단원·학습내용·쪽수·보조쪽수·해당차시·전체차시·준비물 칸에 배경색을
            적용합니다.
          </p>

          <section className="cp-program-cell-bg-section" aria-label="파스텔">
            <div className="cp-program-cell-bg-section-title">파스텔</div>
            <div className="cp-program-cell-bg-preset-grid cp-program-cell-bg-preset-grid--6" role="listbox">
              {PROGRAM_CELL_BG_PASTEL_PRESETS.map((hex) => renderPresetSwatch(hex, hex))}
            </div>
          </section>

          <section className="cp-program-cell-bg-section" aria-label="형광">
            <div className="cp-program-cell-bg-section-title">형광</div>
            <div className="cp-program-cell-bg-preset-grid cp-program-cell-bg-preset-grid--4" role="listbox">
              {PROGRAM_CELL_BG_NEON_PRESETS.map((p) => renderPresetSwatch(p.hex, p.label))}
            </div>
          </section>

          <section className="cp-program-cell-bg-section" aria-label="흐르는 그라데이션">
            <div className="cp-program-cell-bg-section-title">흐르는 그라데이션</div>
            <div className="cp-program-cell-bg-preset-grid cp-program-cell-bg-preset-grid--6" role="listbox">
              {PROGRAM_CELL_BG_ANIM_PRESETS.map((p) => renderPresetSwatch(programCellBgAnimToken(p.id), p.label))}
            </div>
          </section>

          <div className="cp-program-cell-bg-custom-row">
            <label className="cp-program-cell-bg-custom-label">
              기타 색상
              <input
                type="color"
                value={selectedIsAnim ? '#ffffff' : normalizeProgramCellBgValue(selected) ?? '#ffffff'}
                onChange={(e) => setSelected(e.target.value.toLowerCase())}
                className="cp-program-cell-bg-custom-input"
              />
            </label>
            <span
              className={['cp-program-cell-bg-preview', selectedPreview.className].filter(Boolean).join(' ')}
              style={selectedPreview.style}
            >
              {programCellBgAnimLabel(selected) ?? '미리보기'}
            </span>
          </div>

          <div className="cp-program-cell-bg-registered">
            <div className="cp-program-cell-bg-registered-title">등록된 색상</div>
            {registeredColors.length === 0 ? (
              <p className="cp-program-cell-bg-registered-empty">
                적용한 색상이 여기에 등록되어 다음에 바로 쓸 수 있습니다.
              </p>
            ) : (
              <div className="cp-program-cell-bg-registered-list">
                {registeredColors.map((value) => {
                  const inner = programCellBgSwatchProps(value);
                  const isAnim = parseProgramCellBgAnimId(value) !== null;
                  const title = swatchTitle(value);
                  return (
                    <span key={value} className="cp-annual-toolbar-color-wrap">
                      <button
                        type="button"
                        className="cp-dedicated-color-btn"
                        onClick={() => onApply(value)}
                        title={`${title} 바로 적용`}
                        aria-label={`등록 색상 ${title} 적용`}
                      >
                        <span
                          className={['cp-dedicated-color-btn-inner', inner.className].filter(Boolean).join(' ')}
                          style={inner.style}
                          aria-hidden
                        />
                      </button>
                      {isAnim ? (
                        <button
                          type="button"
                          className="cp-annual-toolbar-color-action cp-annual-toolbar-color-action-delete"
                          onClick={() => onRemoveColor(value)}
                          disabled={paletteBusy}
                          aria-label={`등록 색상 ${title} 삭제`}
                          title="등록 해제"
                        >
                          ×
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="cp-annual-toolbar-color-action cp-annual-toolbar-color-action-edit"
                          onClick={() => setEditingHex(value)}
                          disabled={paletteBusy}
                          aria-label={`등록 색상 ${title} 수정`}
                          title="색상 수정·삭제"
                        >
                          <img src={`${process.env.PUBLIC_URL || ''}/edit.svg`} alt="" aria-hidden />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="cp-modal-footer cp-program-cell-bg-footer">
            <button
              type="button"
              className="cp-program-cell-bg-clear"
              onClick={() => onApply(null)}
              title="배경색 지우기"
              aria-label="배경색 지우기"
            />
            <span className="cp-program-cell-bg-footer-spacer" />
            <button type="button" className="cp-btn-secondary" onClick={onClose}>
              취소
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              onClick={applySelected}
              disabled={!normalizeProgramCellBgValue(selected)}
            >
              적용
            </button>
          </div>
        </div>
      </div>
      {editingHex ? (
        <ProgramColorPickModal
          type="dedicated"
          customOnly
          showLabel={false}
          mode="edit"
          initialHex={editingHex}
          existingColors={registeredColors}
          onSubmit={({ hex }) => {
            onReplaceColor(editingHex, hex);
            setEditingHex(null);
          }}
          onDelete={() => {
            onRemoveColor(editingHex);
            setEditingHex(null);
          }}
          onClose={() => setEditingHex(null)}
        />
      ) : null}
    </>
  );
};

export default ProgramCellBgColorModal;
